/**
 * Loan Lifecycle Reconciliation
 *
 * Deterministic preview of the transfer/refinance/discharge classification.
 * Read-only — no per-row sign-off because the matching rule is fully
 * deterministic against raw_commissions (same broker + normalised name +
 * loan amount within 5% + first-paid within 12 months of last-paid).
 *
 * Use this page to sanity-check totals + spot-check sample rows before
 * running the dedicated CLI script's `--execute` against a tenant.
 *
 * @since 2026-04-26
 */

import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { GitBranch, ArrowRightLeft, Repeat, X, AlertCircle, Search } from 'lucide-react';

interface SampleRow {
  event_type: 'discharged' | 'refinanced' | 'transferred';
  loan_id: string;
  entity_code: string;
  customer_name: string | null;
  lender: string | null;
  last_commission_month: string | null;
  successor_loan_id: string | null;
  successor_entity_code: string | null;
  loan_amount_cents: number | null;
}

interface PreviewResponse {
  success: boolean;
  env: 'dev' | 'stage' | 'prod';
  companyId: string;
  absenceThresholdMonths: number;
  counts: { discharged: number; refinanced: number; transferred: number; totalCents: number };
  samples: SampleRow[];
}

const formatMillions = (cents: number) => `$${(cents / 100 / 1_000_000).toFixed(2)}M`;
const formatAmount = (cents: number | null) =>
  cents == null ? '—' : `$${(cents / 100).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

const eventBadge = (type: SampleRow['event_type']) => {
  const cfg = {
    discharged: { Icon: X, label: 'Discharged', color: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
    refinanced: { Icon: Repeat, label: 'Refinanced', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
    transferred: { Icon: ArrowRightLeft, label: 'Transferred', color: 'bg-sky-500/20 text-sky-300 border-sky-500/40' },
  }[type];
  const { Icon, label, color } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const LoanLifecyclePage: React.FC = () => {
  const [companyId, setCompanyId] = useState('');
  const [sampleSize, setSampleSize] = useState(5);
  const [data, setData] = useState<PreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runPreview() {
    if (!companyId.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const callable = httpsCallable<unknown, PreviewResponse>(functions, 'previewLoanLifecycle');
      const result = await callable({ companyId: companyId.trim(), sampleSize });
      setData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setLoading(false);
    }
  }

  const groupedSamples = data
    ? data.samples.reduce<Record<SampleRow['event_type'], SampleRow[]>>(
        (acc, row) => {
          (acc[row.event_type] ||= []).push(row);
          return acc;
        },
        { discharged: [], refinanced: [], transferred: [] },
      )
    : null;

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-sky-600 opacity-90"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-3 mb-3">
            <GitBranch className="w-10 h-10 text-white" />
            <h1 className="text-4xl font-bold text-white">Loan Lifecycle Reconciliation</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Deterministic classification of stale loans into transferred / refinanced / discharged. Read-only preview;
            execution is the dedicated CLI script.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 mb-6">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wide">Company ID</label>
            <input
              type="text"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              placeholder="timms-capital-solutions-..."
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded font-mono text-sm focus:outline-none focus:border-sky-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wide">Samples / bucket</label>
            <input
              type="number"
              value={sampleSize}
              onChange={(e) => setSampleSize(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 5)))}
              min={1}
              max={50}
              className="w-24 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded text-sm focus:outline-none focus:border-sky-500"
            />
          </div>
          <button
            onClick={runPreview}
            disabled={loading || !companyId.trim()}
            className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded font-medium flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {loading ? 'Classifying…' : 'Preview classification'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-rose-900/30 border border-rose-700 rounded-xl p-4 mb-6 text-rose-200 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {/* Results */}
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Discharged" count={data.counts.discharged} icon={X} color="rose" />
            <SummaryCard label="Refinanced" count={data.counts.refinanced} icon={Repeat} color="emerald" />
            <SummaryCard label="Transferred" count={data.counts.transferred} icon={ArrowRightLeft} color="sky" />
            <SummaryCard
              label="Total exposure"
              count={data.counts.discharged + data.counts.refinanced + data.counts.transferred}
              caption={formatMillions(data.counts.totalCents)}
              icon={GitBranch}
              color="slate"
            />
          </div>

          <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 mb-6 text-slate-300 text-sm">
            <div className="font-medium mb-1 text-white">Matching rule (deterministic — no human gate)</div>
            <ul className="space-y-1 list-disc pl-5">
              <li>Loan absent of non-zero commission for ≥{data.absenceThresholdMonths} months</li>
              <li>Successor candidate must share: <strong className="text-white">same broker</strong>, <strong className="text-white">same normalised customer name</strong>, <strong className="text-white">loan amount within ±5%</strong>, <strong className="text-white">first paid within 12 months</strong> of predecessor's last paid</li>
              <li>Successor with upfront commission → <strong className="text-emerald-300">refinanced</strong></li>
              <li>Successor without upfront → <strong className="text-sky-300">transferred</strong></li>
              <li>No successor matching all four criteria → <strong className="text-rose-300">discharged</strong></li>
            </ul>
          </div>

          {(['discharged', 'refinanced', 'transferred'] as const).map((bucket) =>
            groupedSamples && groupedSamples[bucket].length > 0 ? (
              <div key={bucket} className="bg-slate-800/60 border border-slate-700 rounded-xl mb-6 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
                  {eventBadge(bucket)}
                  <span className="text-slate-400 text-sm">
                    showing {groupedSamples[bucket].length} of {data.counts[bucket]} samples
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Loan ID</th>
                      <th className="text-left px-4 py-2 font-medium">Entity</th>
                      <th className="text-left px-4 py-2 font-medium">Customer</th>
                      <th className="text-left px-4 py-2 font-medium">Lender</th>
                      <th className="text-left px-4 py-2 font-medium">Last paid</th>
                      <th className="text-left px-4 py-2 font-medium">Amount</th>
                      <th className="text-left px-4 py-2 font-medium">Successor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedSamples[bucket].map((row) => (
                      <tr
                        key={`${row.loan_id}__${row.entity_code}`}
                        className="border-t border-slate-700/50 hover:bg-slate-900/30"
                      >
                        <td className="px-4 py-2 font-mono text-xs text-white">{row.loan_id}</td>
                        <td className="px-4 py-2 text-slate-300">{row.entity_code}</td>
                        <td className="px-4 py-2 text-slate-200">
                          {row.customer_name ?? <span className="text-slate-500">—</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-300">
                          {row.lender ?? <span className="text-slate-500">—</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-300">
                          {row.last_commission_month ?? <span className="text-slate-500">—</span>}
                        </td>
                        <td className="px-4 py-2 text-slate-300">{formatAmount(row.loan_amount_cents)}</td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {row.successor_loan_id ? (
                            <span className="text-slate-300">
                              {row.successor_loan_id}{' '}
                              <span className="text-slate-500">({row.successor_entity_code})</span>
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null,
          )}
        </>
      )}
    </div>
  );
};

interface SummaryCardProps {
  label: string;
  count: number;
  caption?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'rose' | 'emerald' | 'sky' | 'slate';
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, count, caption, icon: Icon, color }) => {
  const palette = {
    rose: 'from-rose-600/20 to-rose-900/10 border-rose-700/40 text-rose-300',
    emerald: 'from-emerald-600/20 to-emerald-900/10 border-emerald-700/40 text-emerald-300',
    sky: 'from-sky-600/20 to-sky-900/10 border-sky-700/40 text-sky-300',
    slate: 'from-slate-600/20 to-slate-900/10 border-slate-700/40 text-slate-300',
  }[color];
  return (
    <div className={`bg-gradient-to-br ${palette} border rounded-xl p-5`}>
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide opacity-80">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-3xl font-bold text-white">{count.toLocaleString()}</div>
      {caption && <div className="text-sm opacity-70 mt-1">{caption}</div>}
    </div>
  );
};

export default LoanLifecyclePage;
